import React from 'react';
import { Box, Typography } from '@mui/material';
import { useOutletContext, useParams } from 'react-router-dom';
import PillarEngagementOverTime from '../components/PillarBreakdown';
import BrandPillars from '../components/BrandPillars';
import PillarComparisonSection from '../components/PillarComparisonSection';
import PillarEngagementByMonth from '../components/PillarEngagementByMonth';
import TopPosts from '../components/TopPosts';
import { useTopPostsData } from '../hooks/useTopPostsData';
import CircularProgress from '@mui/material/CircularProgress';
import { useFilterContext } from '../context/FilterContext';
import { format } from 'date-fns';

interface FilterProps {
  selectedBrands: string[];
  selectedPlatforms: string[];
  selectedPillars: string[];
  startDate: string | null;
  endDate: string | null;
}

const PillarBreakdownPage: React.FC = () => {
  // Get filters from outlet context (provided by Layout) for backward compatibility
  const filters = useOutletContext<FilterProps>();
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  
  // Get filters from FilterContext directly
  const { 
    selectedBrands, 
    selectedPlatforms, 
    selectedPillars, 
    startDate, 
    endDate 
  } = useFilterContext();
  
  // Format dates for API calls
  const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : filters.startDate;
  const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : filters.endDate;
  
  // NEW: Fetch top posts data using the new hook and relevant filters
  const { 
    data: topPostsData, 
    loading: topPostsLoading, 
    error: topPostsError 
  } = useTopPostsData(
    orgId,
    selectedBrands,    // Pass selected brands
    selectedPillars,   // Pass selected pillars
    selectedPlatforms, // Pass selected platforms
    formattedStartDate,
    formattedEndDate
  );
  
  // Define Loading component locally or import if it exists elsewhere
  const Loading = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
      <CircularProgress />
    </Box>
  );
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '80ch', mt: -1 }}>
        How engagement varies across pillars over time, and how brands perform within each pillar. Toggle between average and total engagement to compare intensity with volume.
      </Typography>

      {/* Components using FilterContext directly */}
      <PillarEngagementOverTime 
        selectedPillars={selectedPillars}
        selectedBrands={selectedBrands}
        startDate={formattedStartDate}
        endDate={formattedEndDate}
      />
      
      <BrandPillars 
        // Empty props are fine since the component uses FilterContext
        selectedBrands={[]}
        selectedPillars={[]}
        startDate={null}
        endDate={null}
      />
      
      <PillarEngagementByMonth 
        selectedPillars={selectedPillars}
        selectedBrands={selectedBrands}
        startDate={formattedStartDate}
        endDate={formattedEndDate}
      />
      
      <PillarComparisonSection
        platforms={selectedPlatforms}
      />
      
      {/* Top Posts Table */}
      {topPostsError ? (
         <Typography color="error">Top posts didn't load: {topPostsError}</Typography>
      ) : (
        <TopPosts
          posts={topPostsData?.posts ?? null}
          total={topPostsData?.total ?? null}
          loading={topPostsLoading}
        />
      )}
    </Box>
  );
};

export default PillarBreakdownPage; 